import { X } from 'lucide-react';
import type { ReactNode } from 'react';

import { cn } from '../lib/utils';
import { Button } from './ui/button';

interface SidePanelProps {
  title: string;
  description?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  className?: string;
}

/**
 * A panel beside a list, not a dialog over it: nothing behind it is dimmed or
 * inert, so the next row is one click away. Wide screens dock it to the right
 * of the page; a phone has no room for both, so there it covers the page.
 * The page lays it out as a sibling of the list inside a flex row.
 */
export function SidePanel({ title, description, onClose, children, className }: SidePanelProps) {
  return (
    <aside
      aria-label={title}
      data-slot="side-panel"
      className={cn(
        'flex min-h-0 flex-col overflow-hidden border-border bg-card',
        'max-lg:fixed max-lg:inset-0 max-lg:z-40 lg:w-[22rem] lg:shrink-0 lg:rounded-lg lg:border',
        className,
      )}
    >
      <header className="flex items-start justify-between gap-3 border-b border-border px-4 py-3">
        <div className="flex min-w-0 flex-col gap-0.5">
          <h2 className="truncate text-base font-semibold">{title}</h2>
          {description ? <div className="text-xs text-muted-foreground">{description}</div> : null}
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label="Close panel"
          onClick={onClose}
        >
          <X className="size-4" />
        </Button>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto p-4">{children}</div>
    </aside>
  );
}
